import React, { useState, useRef } from 'react';
import { StatusSelect, CircularProgressBox } from './styles';
import { Box, CircularProgress, MenuItem } from '@mui/material';
import { get, size } from 'global/lib/lodash';
import sendData from 'redux/actions/sendData';
import SnackAlerts from 'global/components/snack-alerts';
import { UserSelectModal } from 'global/components/userselectmodal';
import { useHashView } from '../../share/useHashView';

const ASSESSMENT_STATUS_OPTIONS = [
  { title: 'Draft', value: 'draft' },
  { title: 'Review', value: 'review' },
  { title: 'Final', value: 'published' },
];

const TEMPLATE_STATUS_OPTIONS = [
  {
    title: 'Template',
    value: 'template',
    description: 'Only visible to the author and users set as Editors.',
  },
  {
    title: 'Global Template',
    value: 'global_template',
    description: (assessment) => `Can be copied by all users in your organization with access to "${get(assessment, 'data.realm.name')}".`,
  },
];

const getStatusOption = (options, currentStatus) => options.find((opt) => opt.value === currentStatus);

const getStatusTitle = (options, currentStatus) => get(getStatusOption(options, currentStatus), 'title', currentStatus);

const getStatusDescription = (option, assessment) => {
  const description = get(option, 'description');
  return typeof description === 'function' ? description(assessment) : description;
};

const StatusBar = (props) => {
  const { assessment, userCanEdit, canEditStatus = userCanEdit, isTemplate } = props;
  const [loading, setLoading] = useState();
  const [alerts, setAlerts] = useState();
  const usref = useRef();

  const { active: hashViewActive } = useHashView();

  const currentStatus = get(assessment, 'data.status');
  const options = isTemplate ? TEMPLATE_STATUS_OPTIONS : ASSESSMENT_STATUS_OPTIONS;
  const currentOption = getStatusOption(options, currentStatus);
  const currentTitle = getStatusTitle(options, currentStatus);
  const helperText = getStatusDescription(currentOption, assessment);
  const statusLoading = loading || !currentStatus;

  const onChange = async (e) => {
    try {
      const value = e.target.value;
      const id = get(assessment, 'data.id');
      const realm_id = get(assessment, 'data.realmId') || get(assessment, 'data.realm.id');
      setLoading(true);

      let notify_users;
      let anonymousView = false;
      let contactIds = [];
      if (!isTemplate && value !== 'draft') {
        const receipt = await get(usref, 'current.displayModal', () => 0)(value);
        const assignedTo = get(receipt, 'assignedTo', []);
        anonymousView = !!get(receipt, 'anyoneWithLink');
        contactIds = get(receipt, 'contactIds', []);
        if (get(receipt, 'sendReceipt', true) && size(assignedTo)) {
          notify_users = assignedTo.map((u) => u.id);
        }
      }

      // Update assessment status. notify_users no longer drives the email — the
      // backend's auto-send was removed in the message_type refactor and is now
      // sent explicitly via /messages below.
      await sendData({
        url: `/${isTemplate ? 'assessment-templates' : 'assessments'}/${id}`,
        method: 'patch',
        data: {
          data: {
            id,
            type: isTemplate ? 'assessment-templates' : 'assessments',
            attributes: {
              status: value,
            },
          },
        },
      });

      // PFM-2564: explicitly POST the status-change message (with message_type
      // required by the new messages contract). Includes optional contacts +
      // anonymous_view for external recipients.
      if (!isTemplate && (size(notify_users) || size(contactIds))) {
        const relationships = {};
        if (size(notify_users)) {
          relationships.users = { data: notify_users.map((uid) => ({ type: 'users', id: uid })) };
        }
        if (size(contactIds)) {
          relationships.contacts = { data: contactIds.map((cid) => ({ type: 'contacts', id: String(cid) })) };
        }
        await sendData({
          url: '/messages',
          method: 'post',
          data: {
            data: {
              type: 'messages',
              attributes: {
                message_type: 'assessment_status_change',
                model_type: 'assessment',
                model_id: id,
                realm_id,
                status: 1,
                title: 'Assessment Status Changed',
                to: '',
                meta: {
                  notify_users: size(notify_users) > 0,
                  anonymous_view: !!anonymousView,
                },
              },
              relationships,
            },
          },
        });
        setAlerts({ message: 'Email sent', severity: 'success' });
      }

      await Promise.all([assessment.update(), get(props, 'scores.update', () => 0)()]);
    } catch (err) {
      setAlerts({ message: `Unable to change status: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!isTemplate && (currentStatus === 'published' || !userCanEdit || hashViewActive)) {
    return null;
  }

  const assignees = [...get(assessment, 'data.editors', []), ...get(assessment, 'data.viewers', []), ...get(assessment, 'data.assignees', [])];

  return (
    <Box>
      {statusLoading ? (
        <CircularProgressBox>
          <CircularProgress size={18} />
        </CircularProgressBox>
      ) : canEditStatus ? (
        <StatusSelect
          label="Status"
          value={currentStatus}
          select
          slotProps={{
            input: { disableUnderline: true },
            select: {
              renderValue: (selected) => getStatusTitle(options, selected),
            },
          }}
          onChange={onChange}
          size="small"
          helperText={helperText}
        >
          {options.map((opt, i) => {
            return (
              <MenuItem key={i} value={get(opt, 'value')}>
                {opt.title}
              </MenuItem>
            );
          })}
        </StatusSelect>
      ) : (
        <StatusSelect label="Status" value={currentTitle} slotProps={{ input: { disableUnderline: true, readOnly: true } }} size="small" helperText={helperText} />
      )}

      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />

      {!isTemplate && <UserSelectModal ref={usref} assignees={assignees} realm_id={get(assessment, 'data.realmId')} allowUpdateOnly />}
    </Box>
  );
};

export default StatusBar;
